import type { Schema } from './schema'
import { orderedProperties, resolve } from './schema'
import type { WebsiteMenuFilter } from './website'

// What a generated route will not serve, said to the author.
//
// **Delivery drops these silently, and only here can they be said.** A
// generated route serves every member of its source that its page pattern
// accepts and that has a key no other member shares. Everything else has
// no page, no menu entry and no record link, and a visitor meets a 404 —
// correct, because picking one of two records sharing a key would be
// arbitrary, and a route cannot refuse a write to a record it has never
// seen. So the checks the composer makes when it enumerates
// (`compose/routes.go`, `items` and `menuFilter`) are repeated here, over
// the same members, and each failure becomes a line an author can act on.
//
// Pure: the reads are the caller's. That keeps the rules in one place a
// test can hold, and makes the screen a list of these lines.

/** One source member, as its own document reads. */
export interface RouteMember {
  id: string
  type: string
  label?: string
  fields?: Record<string, unknown>
}

/** What the report needs, every part of which the caller read. */
export interface RouteReportInput {
  /** The record type the page pattern accepts. */
  inputType: string
  /** The field that type declares as its key; absent when it names none. */
  key?: string
  /** The accepted type's field schema, off the manifest. */
  fields?: Schema
  members: RouteMember[]
  filters: WebsiteMenuFilter[]
}

/**
 * Every reason a member, or a menu tag, will not be served.
 *
 * Empty when the route serves every member it holds.
 */
export function generatedRouteProblems(input: RouteReportInput): string[] {
  const problems: string[] = []
  const name = (member: RouteMember) => member.label || member.id

  const accepted = input.members.filter((member) => member.type === input.inputType)
  const other = input.members.length - accepted.length
  if (other > 0) {
    problems.push(
      `${other} ${other === 1 ? 'member is' : 'members are'} not ${input.inputType}, the type the page pattern accepts, and ${other === 1 ? 'has' : 'have'} no page.`,
    )
  }

  if (!input.key) {
    if (accepted.length > 0) {
      problems.push(`${input.inputType} declares no key field, so none of its records has an address and this route serves nothing.`)
    }
  } else {
    const byKey = new Map<string, RouteMember[]>()
    for (const member of accepted) {
      const value = member.fields?.[input.key]
      if (typeof value !== 'string' || value === '') {
        problems.push(`${name(member)} has no ${input.key}, so it has no page.`)
        continue
      }
      byKey.set(value, [...(byKey.get(value) ?? []), member])
    }
    for (const [key, sharing] of byKey) {
      if (sharing.length > 1) {
        problems.push(
          `${sharing.map(name).join(' and ')} share the ${input.key} "${key}", so none of them has a page until one changes.`,
        )
      }
    }
  }

  for (const filter of input.filters) {
    const reason = filterProblem(filter, input.fields)
    if (reason) problems.push(`The ${filter.tag} filter ${reason}, so the tag takes no records from this route.`)
  }
  return problems
}

/**
 * Why a menu filter cannot narrow the accepted type, or nothing when it can.
 *
 * `component.Filter.Check`, read off the manifest's schema instead of the
 * declaration: a boolean is a `boolean`, an enum is a string with an
 * `enum`, and nothing else divides a collection.
 */
export function filterProblem(filter: WebsiteMenuFilter, fields: Schema | undefined): string | undefined {
  const field = filter.where.field
  if (!fields) return undefined
  const property = orderedProperties(fields, fields).find((entry) => entry.name === field)
  if (!property) return `names ${field || 'no field'}, which the record type does not declare`
  const schema = resolve(property.schema, fields)
  const equals = filter.where.equals
  if (schema?.type === 'boolean') {
    return typeof equals === 'boolean' ? undefined : `compares the boolean ${field} with "${String(equals)}"`
  }
  if (schema?.enum) {
    return typeof equals === 'string' && schema.enum.includes(equals)
      ? undefined
      : `compares ${field} with "${String(equals)}", which is not one of its values`
  }
  return `names ${field}, which is neither a boolean nor an enum`
}
